import type {AudioClipConfig, AudioTrackConfig, Scene} from '@cascade/core';
import {generateAudioId} from '@cascade/core';
import clsx from 'clsx';
import {createPortal} from 'react-dom';
import React, {
  type HTMLAttributes,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useApplication, useTimelineContext} from '../../contexts';
import {useScenes, useSubscribableValue} from '../../hooks';
import {useSaaSEditor} from '../../saas/contexts/editor-context';
import {audioAnalysisClient} from '../../saas/audio-analysis-client';
import * as db from '../../saas/db';
import {MouseButton} from '../../utils';
import styles from './Timeline.module.scss';
import {TTSModal} from './TTSModal';

// ─── Analysis Types (mirror of backend AnalyzedAudio) ───

interface WordTiming {
  word: string;
  start: number;
  end: number;
}

interface SentenceTiming {
  text: string;
  start: number;
  end: number;
  wordStartIndex: number;
  wordEndIndex: number;
}

interface AnalyzedAudio {
  meta: {duration: number; sampleRate: number; filename: string};
  transcript: {text: string; words: WordTiming[]; sentences: SentenceTiming[]};
  pauses: {start: number; end: number; duration: number; type: string}[];
}

type AnalysisStatus = 'none' | 'checking' | 'loading' | 'analyzed';

interface ClipAnalysisState {
  status: AnalysisStatus;
  data?: AnalyzedAudio;
}

/** Module-level cache so analysis state persists across re-renders */
const analysisCache = new Map<string, ClipAnalysisState>();

async function checkAnalysisCache(audioPath: string): Promise<AnalyzedAudio | null> {
  try {
    const res = await fetch(
      `/__cascade/audio/analysis?path=${encodeURIComponent(audioPath)}`,
    );
    if (res.status === 200) {
      const data = await res.json();
      if (data.meta) return data as AnalyzedAudio;
    }
  } catch { /* ignore */ }
  return null;
}

async function triggerAnalysis(
  audioPath: string,
  transcript?: string,
): Promise<AnalyzedAudio | null> {
  try {
    const body: Record<string, string> = {audioPath};
    if (transcript) body.transcript = transcript;
    const res = await fetch('/__cascade/audio/analyze', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.meta) return data as AnalyzedAudio;
    }
  } catch { /* ignore */ }
  return null;
}

const HEIGHT = 20;

// ─── Clip Color System ───

const CLIP_COLORS = [
  '#ff8a80', // coral
  '#64ffda', // teal
  '#ffd740', // amber
  '#b388ff', // lavender
  '#69f0ae', // mint
  '#40c4ff', // sky
  '#ff9e80', // peach
  '#ea80fc', // lilac
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function getClipColor(src: string): string {
  return CLIP_COLORS[hashString(src) % CLIP_COLORS.length];
}

function hexToRgba(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

// ─── Context Menu State ───
interface AudioContextMenuState {
  x: number;
  y: number;
  label: string;
  clipId: string;
  clipSrc: string;
  color: string;
  analysisStatus: AnalysisStatus;
}

// ─── Audio Track Row (renders clips for a single track) ───

interface AudioTrackRowProps {
  track: AudioTrackConfig;
  clips: AudioClipConfig[];
}

export function AudioTrackRow({track, clips}: AudioTrackRowProps) {
  const {player} = useApplication();
  const scenes = useScenes();
  const saas = useSaaSEditor();
  const isSaaS = !!saas;
  const [contextMenu, setContextMenu] = useState<AudioContextMenuState | null>(
    null,
  );
  const [analysisStates, setAnalysisStates] = useState<
    Map<string, ClipAnalysisState>
  >(() => new Map(analysisCache));

  // Check for cached analysis on mount and when clips change
  useEffect(() => {
    if (!clips || clips.length === 0) return;

    for (const clip of clips) {
      const existing = analysisStates.get(clip.src) ?? analysisCache.get(clip.src);
      if (existing && existing.status !== 'none') continue;

      // SaaS mode: check persisted analyses from Supabase settings,
      // then fall back to in-memory cache, then mark as none
      if (isSaaS) {
        // Check persisted analysis from project settings
        const persisted = saas?.audioAnalyses?.[clip.src] as
          | AnalyzedAudio
          | undefined;
        if (persisted) {
          analysisCache.set(clip.src, {status: 'analyzed', data: persisted});
          setAnalysisStates(prev => {
            const next = new Map(prev);
            next.set(clip.src, {status: 'analyzed', data: persisted});
            return next;
          });
          continue;
        }

        const inMemory = analysisCache.get(clip.src);
        if (!inMemory || inMemory.status === 'none') {
          analysisCache.set(clip.src, {status: 'none'});
          setAnalysisStates(prev => {
            const next = new Map(prev);
            next.set(clip.src, {status: 'none'});
            return next;
          });
        }
        continue;
      }

      // Dev mode: check server cache
      analysisCache.set(clip.src, {status: 'checking'});
      setAnalysisStates(prev => {
        const next = new Map(prev);
        next.set(clip.src, {status: 'checking'});
        return next;
      });

      checkAnalysisCache(clip.src).then(data => {
        const state: ClipAnalysisState = data
          ? {status: 'analyzed', data}
          : {status: 'none'};
        analysisCache.set(clip.src, state);
        setAnalysisStates(prev => {
          const next = new Map(prev);
          next.set(clip.src, state);
          return next;
        });
      });
    }
  }, [clips, isSaaS, saas]);

  // Close context menu on click anywhere
  useEffect(() => {
    if (!contextMenu) return;
    const handler = (e: PointerEvent) => {
      const menu = document.querySelector(`.${styles.contextMenu}`);
      if (menu?.contains(e.target as Node)) return;
      setContextMenu(null);
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [contextMenu]);

  const handleContextMenu = useCallback(
    (event: MouseEvent, clipId: string, clipSrc: string, label: string, color: string) => {
      event.preventDefault();
      event.stopPropagation();
      const state = analysisStates.get(clipSrc) ?? analysisCache.get(clipSrc);
      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        label,
        clipId,
        clipSrc,
        color,
        analysisStatus: state?.status ?? 'none',
      });
    },
    [analysisStates],
  );

  const handleRemoveAudio = useCallback(() => {
    if (!contextMenu) return;
    setContextMenu(null);
    player.removeAudioClip(contextMenu.clipId);
  }, [player, contextMenu]);

  const handleAnalyze = useCallback(() => {
    if (!contextMenu) return;
    const src = contextMenu.clipSrc;
    setContextMenu(null);

    // Mark as loading
    analysisCache.set(src, {status: 'loading'});
    setAnalysisStates(prev => {
      const next = new Map(prev);
      next.set(src, {status: 'loading'});
      return next;
    });

    const updateState = (data: AnalyzedAudio | null) => {
      const state: ClipAnalysisState = data
        ? {status: 'analyzed', data}
        : {status: 'none'};
      analysisCache.set(src, state);
      setAnalysisStates(prev => {
        const next = new Map(prev);
        next.set(src, state);
        return next;
      });
    };

    if (isSaaS) {
      // Browser-side analysis via Whisper Web Worker
      audioAnalysisClient
        .analyze(src)
        .then(data => {
          updateState(data);
          // Persist analysis result to Supabase and update context
          if (data && saas) {
            db.saveAudioAnalysis(
              saas.supabase,
              saas.projectId,
              src,
              data,
            ).catch(err =>
              console.error('[Cascade] Failed to save analysis:', err),
            );
            saas.onAudioAnalysisUpdated(src, data);
          }
        })
        .catch(() => updateState(null));
    } else {
      // Server-side analysis via Vite dev server
      triggerAnalysis(src).then(updateState);
    }
  }, [contextMenu, isSaaS, saas]);

  const hasClips = clips && clips.length > 0;

  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer?.files?.[0];
      if (file && file.type.startsWith('audio/')) {
        if (isSaaS && saas) {
          // SaaS: upload to Supabase Storage
          db.uploadAudio(saas.supabase, saas.projectId, file.name, file)
            .then(url => {
              player.addAudioClip({
                id: generateAudioId(),
                src: url,
                offset: 0,
                trackId: track.id,
              });
            })
            .catch(err => console.error('Audio upload failed:', err));
        } else {
          // Dev server: upload via Vite endpoint
          fetch('/__cascade/upload-audio', {
            method: 'POST',
            body: file,
            headers: {'x-filename': file.name},
          })
            .then(res => res.json())
            .then(data => {
              if (data.url) player.addAudioClip({
                id: generateAudioId(),
                src: data.url,
                offset: 0,
                trackId: track.id,
              });
            })
            .catch(err => console.error('Audio upload failed:', err));
        }
      }
    },
    [player, track.id, isSaaS, saas],
  );

  return (
    <>
      <div
        className={clsx(
          styles.audioTrack,
          isDragOver && styles.audioTrackDragOver,
        )}
        onDragOver={e => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        {hasClips &&
          clips.map(clip => (
            <ProjectAudioClip
              key={clip.id}
              clip={clip}
              analysisStatus={
                (analysisStates.get(clip.src) ?? analysisCache.get(clip.src))
                  ?.status ?? 'none'
              }
              onContextMenu={handleContextMenu}
            />
          ))}
        {hasClips &&
          scenes.map(scene => <AudioGroup key={scene.name} scene={scene} />)}
        {isDragOver && (
          <div className={styles.audioDropOverlay}>Drop audio here</div>
        )}
      </div>
      {contextMenu && (
        <AudioClipContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          label={contextMenu.label}
          color={contextMenu.color}
          analysisStatus={contextMenu.analysisStatus}
          onRemove={handleRemoveAudio}
          onAnalyze={handleAnalyze}
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  );
}

// ─── Audio Context Menu ───

interface AudioClipContextMenuProps {
  x: number;
  y: number;
  label: string;
  color: string;
  analysisStatus: AnalysisStatus;
  onRemove: () => void;
  onAnalyze: () => void;
  onClose: () => void;
}

function AudioClipContextMenu({
  x,
  y,
  label,
  color,
  analysisStatus,
  onRemove,
  onAnalyze,
  onClose,
}: AudioClipContextMenuProps) {
  const analyzeLabel =
    analysisStatus === 'loading'
      ? 'Analyzing...'
      : analysisStatus === 'analyzed'
        ? 'Re-analyze audio'
        : 'Analyze audio';

  return (
    <div
      className={styles.contextMenu}
      style={{left: `${x}px`, top: `${y}px`}}
      onPointerDown={e => e.stopPropagation()}
    >
      <div className={styles.contextMenuHeader}>
        <span
          className={styles.contextMenuDot}
          style={{backgroundColor: color}}
        />
        {label}
        {analysisStatus === 'analyzed' && (
          <span className={styles.contextMenuBadge}>analyzed</span>
        )}
      </div>
      <button
        className={styles.contextMenuItem}
        onClick={onAnalyze}
        disabled={analysisStatus === 'loading'}
      >
        {analyzeLabel}
      </button>
      <button className={styles.contextMenuItem} onClick={onRemove}>
        Remove audio
      </button>
      <button className={styles.contextMenuItem} onClick={onClose}>
        Cancel
      </button>
    </div>
  );
}

// ─── Project Audio Clip (draggable, per-clip) ───

interface ProjectAudioClipProps {
  clip: AudioClipConfig;
  analysisStatus: AnalysisStatus;
  onContextMenu: (
    event: MouseEvent,
    clipId: string,
    clipSrc: string,
    label: string,
    color: string,
  ) => void;
}

function ProjectAudioClip({
  clip,
  analysisStatus,
  onContextMenu,
}: ProjectAudioClipProps) {
  const {player} = useApplication();
  const [editingOffset, setEditingOffset] = useState(0);
  const {pixelsToSeconds} = useTimelineContext();
  const fullOffset = Math.max(0, clip.offset + editingOffset);
  const color = useMemo(() => getClipColor(clip.src), [clip.src]);
  const label = clip.src.split('/').pop() ?? 'Audio';

  useLayoutEffect(() => {
    setEditingOffset(0);
  }, [clip.offset]);

  return (
    <AudioClip
      editable
      audio={clip.src}
      offset={fullOffset}
      disabled
      color={color}
      label={label}
      analysisStatus={analysisStatus}
      onContextMenu={e => {
        onContextMenu(e as unknown as MouseEvent, clip.id, clip.src, label, color);
      }}
      onPointerDown={e => {
        if (e.button === MouseButton.Right) return;
        if (e.button === MouseButton.Left) {
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.setPointerCapture(e.pointerId);
        }
      }}
      onPointerMove={e => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          const next = editingOffset + pixelsToSeconds(e.movementX);
          setEditingOffset(Math.max(-clip.offset, next));
        }
      }}
      onPointerUp={e => {
        if (e.button === MouseButton.Left) {
          e.currentTarget.releasePointerCapture(e.pointerId);
          if (editingOffset !== 0) {
            player.setAudioClipOffset(clip.id, fullOffset);
          }
          setEditingOffset(0);
        }
      }}
    />
  );
}

// ─── Audio Groups (per-scene sounds) ───

interface AudioGroupProps {
  scene: Scene;
}

export function AudioGroup({scene}: AudioGroupProps) {
  const sounds = useSubscribableValue(scene.sounds.onChanged);
  return (
    <>
      {sounds.map(sound => (
        <AudioClip hoverable {...sound} />
      ))}
    </>
  );
}

// ─── Track Volume Control (speaker icon + hover-reveal vertical slider) ───

// Speaker icons for different volume levels
const SpeakerMuted = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z" />
  </svg>
);
const SpeakerLow = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 9v6h4l5 5V4l-5 5H7z" />
  </svg>
);
const SpeakerMid = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-3.02z" />
  </svg>
);
const SpeakerHigh = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);

function SpeakerIcon({volume, muted}: {volume: number; muted: boolean}) {
  if (muted || volume === 0) return <SpeakerMuted />;
  if (volume <= 0.33) return <SpeakerLow />;
  if (volume <= 0.66) return <SpeakerMid />;
  return <SpeakerHigh />;
}

interface TrackVolumeControlProps {
  volume: number;
  muted: boolean;
  onToggleMute: () => void;
  onVolumeChange: (volume: number) => void;
}

function TrackVolumeControl({
  volume,
  muted,
  onToggleMute,
  onVolumeChange,
}: TrackVolumeControlProps) {
  const [showSlider, setShowSlider] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [sliderPos, setSliderPos] = useState({left: 0, bottom: 0});
  const isHoveredRef = useRef(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateSliderPos = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setSliderPos({
        left: rect.left + rect.width / 2,
        bottom: window.innerHeight - rect.top + 4,
      });
    }
  }, []);

  const scheduleHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (!isHoveredRef.current && !isDragging) {
        setShowSlider(false);
      }
    }, 200);
  }, [isDragging]);

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    isHoveredRef.current = true;
    cancelHide();
    updateSliderPos();
    setShowSlider(true);
  }, [cancelHide, updateSliderPos]);

  const handleMouseLeave = useCallback(() => {
    isHoveredRef.current = false;
    if (!isDragging) scheduleHide();
  }, [isDragging, scheduleHide]);

  // When drag ends, check if mouse is still hovering
  useEffect(() => {
    if (!isDragging && showSlider && !isHoveredRef.current) {
      scheduleHide();
    }
  }, [isDragging, showSlider, scheduleHide]);

  const volumeFromPointer = useCallback(
    (clientY: number) => {
      const el = trackRef.current;
      if (!el) return volume;
      const rect = el.getBoundingClientRect();
      const ratio = 1 - (clientY - rect.top) / rect.height;
      return Math.round(Math.max(0, Math.min(1, ratio)) * 100) / 100;
    },
    [volume],
  );

  const handleTrackPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      setIsDragging(true);
      onVolumeChange(volumeFromPointer(e.clientY));
    },
    [onVolumeChange, volumeFromPointer],
  );

  const handleTrackPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      onVolumeChange(volumeFromPointer(e.clientY));
    },
    [isDragging, onVolumeChange, volumeFromPointer],
  );

  const handleTrackPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      setIsDragging(false);
      // Check if pointer is still inside container
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        if (
          e.clientX < rect.left || e.clientX > rect.right ||
          e.clientY < rect.top || e.clientY > rect.bottom
        ) {
          isHoveredRef.current = false;
        }
      }
    },
    [isDragging],
  );

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.05 : 0.05;
      const newVol = Math.round(Math.max(0, Math.min(1, volume + delta)) * 100) / 100;
      onVolumeChange(newVol);
    },
    [volume, onVolumeChange],
  );

  const pct = Math.round(volume * 100);

  // Portal the slider hover zone: close when pointer leaves the portal
  const handleSliderMouseEnter = useCallback(() => {
    isHoveredRef.current = true;
    cancelHide();
  }, [cancelHide]);

  const handleSliderMouseLeave = useCallback(() => {
    isHoveredRef.current = false;
    if (!isDragging) scheduleHide();
  }, [isDragging, scheduleHide]);

  return (
    <div
      ref={containerRef}
      className={styles.volumeControl}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Slider popover -- portaled to body to escape overflow:hidden */}
      {showSlider && createPortal(
        <div
          className={clsx(styles.volumeSlider, isDragging && styles.volumeSliderDragging)}
          style={{
            left: `${sliderPos.left}px`,
            bottom: `${sliderPos.bottom}px`,
          }}
          onMouseEnter={handleSliderMouseEnter}
          onMouseLeave={handleSliderMouseLeave}
          onWheel={handleWheel}
        >
          <div className={styles.volumeSliderLabel}>{pct}%</div>
          <div
            ref={trackRef}
            className={styles.volumeSliderTrack}
            onPointerDown={handleTrackPointerDown}
            onPointerMove={handleTrackPointerMove}
            onPointerUp={handleTrackPointerUp}
          >
            <div
              className={styles.volumeSliderFill}
              style={{height: `${pct}%`}}
            />
            <div
              className={styles.volumeSliderThumb}
              style={{bottom: `${pct}%`}}
            />
          </div>
        </div>,
        document.body,
      )}

      {/* Speaker button */}
      <button
        className={clsx(
          styles.trackMuteButton,
          muted && styles.trackMuted,
        )}
        onClick={onToggleMute}
        title={
          muted
            ? 'Unmute track'
            : `Volume: ${pct}% — Click to mute`
        }
      >
        <SpeakerIcon volume={volume} muted={muted} />
      </button>
    </div>
  );
}

// ─── Audio Track Row Header (per-track: name, mute, volume, add clip) ───

interface AudioTrackRowHeaderProps {
  track: AudioTrackConfig;
}

export function AudioTrackRowHeader({track}: AudioTrackRowHeaderProps) {
  const {player} = useApplication();
  const saas = useSaaSEditor();
  const isSaaS = !!saas;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [ttsOpen, setTtsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('audio/')) return;
      try {
        let url: string;
        if (isSaaS && saas) {
          // SaaS: upload to Supabase Storage
          url = await db.uploadAudio(saas.supabase, saas.projectId, file.name, file);
        } else {
          // Dev server: upload via Vite endpoint
          const res = await fetch('/__cascade/upload-audio', {
            method: 'POST',
            body: file,
            headers: {'x-filename': file.name},
          });
          const data = await res.json();
          url = data.url;
        }
        if (url) {
          player.addAudioClip({
            id: generateAudioId(),
            src: url,
            offset: 0,
            trackId: track.id,
          });
        }
      } catch (err) {
        console.error('Audio upload failed:', err);
      }
    },
    [player, track.id, isSaaS, saas],
  );

  const handleTTSGenerate = useCallback(
    async (url: string, transcript?: string) => {
      let finalUrl = url;

      // In SaaS mode, upload blob URLs to Supabase Storage for persistence
      if (isSaaS && saas && url.startsWith('blob:')) {
        try {
          const resp = await fetch(url);
          const blob = await resp.blob();
          const ext = blob.type.includes('wav') ? 'wav' : 'mp3';
          finalUrl = await db.uploadAudio(
            saas.supabase,
            saas.projectId,
            `tts-${Date.now()}.${ext}`,
            blob,
          );
          URL.revokeObjectURL(url);
        } catch (err) {
          console.error('Failed to upload TTS audio:', err);
          // Fall back to blob URL (will work for this session only)
        }
      }

      player.addAudioClip({
        id: generateAudioId(),
        src: finalUrl,
        offset: 0,
        trackId: track.id,
      });
      setTtsOpen(false);

      // Fire-and-forget: auto-analyze with known transcript for word timing.
      // In SaaS mode this runs browser-side Whisper; mirrors the dev-mode
      // server-side analysis that TTSModal.tsx fires after generation.
      if (isSaaS && transcript) {
        audioAnalysisClient
          .analyze(finalUrl, transcript)
          .then(analysis => {
            analysisCache.set(finalUrl, {status: 'analyzed', data: analysis});
            // Persist to Supabase and update context
            if (saas) {
              db.saveAudioAnalysis(
                saas.supabase,
                saas.projectId,
                finalUrl,
                analysis,
              ).catch(err =>
                console.error('[Cascade] Failed to save analysis:', err),
              );
              saas.onAudioAnalysisUpdated(finalUrl, analysis);
            }
          })
          .catch(err => {
            console.error('[Cascade] Auto-analysis failed:', err);
          });
      }
    },
    [player, track.id, isSaaS, saas],
  );

  const handleToggleMute = useCallback(() => {
    player.updateAudioTrack(track.id, {muted: !track.muted});
  }, [player, track.id, track.muted]);

  const handleNameSubmit = useCallback(() => {
    if (nameInputRef.current) {
      const newName = nameInputRef.current.value.trim();
      if (newName && newName !== track.name) {
        player.updateAudioTrack(track.id, {name: newName});
      }
    }
    setIsEditing(false);
  }, [player, track.id, track.name]);

  const handleRemoveTrack = useCallback(() => {
    player.removeAudioTrack(track.id);
  }, [player, track.id]);

  return (
    <div
      className={styles.audioTrackHeader}
      onContextMenu={e => {
        e.preventDefault();
        handleRemoveTrack();
      }}
    >
      <div className={styles.trackHeaderContent}>
        <TrackVolumeControl
          volume={track.volume}
          muted={track.muted}
          onToggleMute={handleToggleMute}
          onVolumeChange={v => player.updateAudioTrack(track.id, {volume: v})}
        />

        {/* Add clip button */}
        <button
          ref={buttonRef}
          className={clsx(
            styles.trackAddButton,
            popoverOpen && styles.trackAddButtonActive,
          )}
          onClick={() => setPopoverOpen(!popoverOpen)}
          title="Add audio to track"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
          </svg>
        </button>
      </div>

      {popoverOpen && (
        <AudioAddPopover
          anchorRef={buttonRef}
          onUpload={() => {
            setPopoverOpen(false);
            fileInputRef.current?.click();
          }}
          onVoiceover={() => {
            setPopoverOpen(false);
            setTtsOpen(true);
          }}
          onClose={() => setPopoverOpen(false)}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        style={{display: 'none'}}
        onChange={e => {
          const file = (e.target as HTMLInputElement).files?.[0];
          if (file) handleUpload(file);
        }}
      />

      {createPortal(
        <TTSModal
          open={ttsOpen}
          onClose={() => setTtsOpen(false)}
          onGenerate={handleTTSGenerate}
        />,
        document.body,
      )}
    </div>
  );
}

// ─── Add Track Button (renders below the last track header) ───

export function AddTrackHeader() {
  const {player} = useApplication();

  const handleAddTrack = useCallback(() => {
    const tracks = player.onAudioTracksChanged.current as AudioTrackConfig[];
    const trackNumber = tracks.length + 1;
    player.addAudioTrack({
      id: generateAudioId(),
      name: `Track ${trackNumber}`,
      volume: 1,
      muted: false,
    });
  }, [player]);

  return (
    <div className={styles.addTrackHeader}>
      <button
        className={styles.addTrackButton}
        onClick={handleAddTrack}
        title="Add audio track"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
        </svg>
      </button>
    </div>
  );
}

// ─── Audio Add Popover ───

interface AudioAddPopoverProps {
  anchorRef: {current: HTMLElement | null};
  onUpload: () => void;
  onVoiceover: () => void;
  onClose: () => void;
}

function AudioAddPopover({
  anchorRef,
  onUpload,
  onVoiceover,
  onClose,
}: AudioAddPopoverProps) {
  const [pos, setPos] = useState({x: 0, y: 0});

  useLayoutEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({x: rect.right + 8, y: rect.top - 4});
    }
  }, []);

  useEffect(() => {
    const handler = (e: PointerEvent) => {
      const popover = document.querySelector(`.${styles.audioAddPopover}`);
      if (popover?.contains(e.target as Node)) return;
      const button = anchorRef.current;
      if (button?.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [onClose]);

  return (
    <div
      className={styles.audioAddPopover}
      style={{left: `${pos.x}px`, top: `${pos.y}px`}}
      onPointerDown={e => e.stopPropagation()}
    >
      <button className={styles.audioAddOption} onPointerUp={onUpload}>
        <span className={styles.audioAddOptionIcon}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </span>
        <span className={styles.audioAddOptionText}>
          <span className={styles.audioAddOptionLabel}>Upload audio file</span>
          <span className={styles.audioAddOptionSub}>.mp3, .wav, .ogg</span>
        </span>
      </button>
      <button className={styles.audioAddOption} onPointerUp={onVoiceover}>
        <span className={styles.audioAddOptionIcon}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6zM10 19a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
          </svg>
        </span>
        <span className={styles.audioAddOptionText}>
          <span className={styles.audioAddOptionLabel}>AI voiceover</span>
          <span className={styles.audioAddOptionSub}>
            Type a script, pick a voice
          </span>
        </span>
      </button>
    </div>
  );
}

// ─── Audio Clip (waveform renderer) ───

interface AudioClipProps extends HTMLAttributes<HTMLDivElement> {
  audio: string;
  offset: number;
  start?: number;
  end?: number;
  realPlaybackRate?: number;
  hoverable?: boolean;
  editable?: boolean;
  disabled?: boolean;
  color?: string;
  label?: string;
  analysisStatus?: AnalysisStatus;
}

export function AudioClip({
  audio,
  offset,
  start = 0,
  end = Infinity,
  realPlaybackRate = 1,
  hoverable,
  editable,
  disabled,
  color,
  label,
  analysisStatus,
  className,
  ...props
}: AudioClipProps) {
  const {player} = useApplication();
  const audioData = useSubscribableValue(
    player.audioResources.get(audio).onData,
  );

  const ref = useRef<HTMLCanvasElement>();
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const {
    viewLength,
    firstVisibleTime,
    lastVisibleTime,
    density,
    secondsToPercents,
    secondsToPixels,
  } = useTimelineContext();

  const {
    waveformWidth,
    waveformStart,
    waveformEnd,
    clipStart,
    clipDuration,
    waveformVisible,
  } = useMemo(() => {
    const endOffset =
      (Math.min(audioData.duration, end) - start) / realPlaybackRate;

    const clipStart = offset;
    const clipEnd = offset + endOffset;

    const waveformStart = Math.max(firstVisibleTime, clipStart);
    const waveformEnd = Math.min(lastVisibleTime, clipEnd);

    const duration = waveformEnd - waveformStart;
    const waveformVisible =
      waveformStart < waveformEnd &&
      waveformEnd > firstVisibleTime &&
      waveformStart < lastVisibleTime;

    return {
      waveformWidth: secondsToPixels(duration),
      waveformStart,
      waveformEnd,
      clipStart,
      clipDuration: clipEnd - clipStart,
      waveformVisible,
    };
  }, [
    audioData,
    end,
    start,
    firstVisibleTime,
    lastVisibleTime,
    secondsToPixels,
    realPlaybackRate,
    offset,
  ]);

  useLayoutEffect(() => {
    if (!waveformVisible || !ref.current) return;
    if (!contextRef.current || contextRef.current?.canvas !== ref.current) {
      contextRef.current = ref.current?.getContext('2d');
    }

    const context = contextRef.current;
    if (!context) return;
    context.clearRect(0, 0, viewLength, HEIGHT * 2);
    context.beginPath();
    context.moveTo(0, HEIGHT);

    const relativeStartTime =
      (waveformStart - offset) * realPlaybackRate + start;
    const relativeEndTime = (waveformEnd - offset) * realPlaybackRate + start;

    const startSample = relativeStartTime * audioData.sampleRate;
    const endSample = relativeEndTime * audioData.sampleRate;

    const step = Math.ceil(density * 4);
    const flooredStart = Math.floor(startSample / step / 2) * step * 2;
    const padding = flooredStart - startSample;
    const length = endSample - startSample;

    for (let index = startSample; index <= endSample; index += step * 2) {
      const offset = index - startSample;
      const sample = Math.floor(flooredStart + offset);
      if (sample >= audioData.peaks.length) break;

      context.lineTo(
        ((padding + offset) / length) * waveformWidth,
        (audioData.peaks[sample] / audioData.absoluteMax) * HEIGHT + HEIGHT,
      );
      context.lineTo(
        ((padding + offset + step) / length) * waveformWidth,
        (audioData.peaks[sample + 1] / audioData.absoluteMax) * HEIGHT + HEIGHT,
      );
    }

    context.lineWidth = 1;
    context.lineJoin = 'round';
    context.strokeStyle = color ?? '#fff';
    context.stroke();
  }, [
    waveformStart,
    waveformEnd,
    waveformWidth,
    waveformVisible,
    offset,
    density,
    viewLength,
    start,
    audioData,
    realPlaybackRate,
    color,
  ]);

  const [wrapperStyle, canvasStyle] = useMemo(
    () => [
      {
        left: `${secondsToPercents(clipStart)}%`,
        width: `${secondsToPercents(clipDuration)}%`,
        height: `${HEIGHT * 2}px`,
        ...(color
          ? {
              backgroundColor: hexToRgba(color, 0.08),
              borderRadius: '4px',
            }
          : {}),
      },
      {
        left: `${((waveformStart - clipStart) / clipDuration) * 100}%`,
      },
    ],
    [waveformStart, clipDuration, clipStart, secondsToPercents, color],
  );

  return (
    <div
      className={clsx(
        styles.audioClip,
        hoverable && styles.hoverable,
        editable && styles.editable,
        className,
      )}
      style={wrapperStyle}
      {...props}
    >
      {editable && label && (
        <div
          className={styles.clipLabel}
          style={{backgroundColor: color ?? 'rgba(255,255,255,0.5)'}}
        >
          {label}
          {analysisStatus === 'analyzed' && (
            <span className={styles.clipAnalyzedDot} />
          )}
          {analysisStatus === 'loading' && (
            <span className={styles.clipAnalyzingDot} />
          )}
        </div>
      )}
      {hoverable && waveformWidth > 8 && (
        <div className={styles.audioLabel}>{audio}</div>
      )}
      {waveformVisible && waveformWidth > 8 && (
        <canvas
          ref={ref}
          style={canvasStyle}
          className={styles.audioCanvas}
          width={Math.round(waveformWidth)}
          height={HEIGHT * 2}
        />
      )}
    </div>
  );
}
